import { enabledSizes, formatPercent, isKomboEnabled, komboSizeCm, T, tt, type PizzaConfig, type PizzaSize } from '@agos/shared'
import { ConfirmDialog, cn, BUTTON_PRIMARY} from '@agos/ui'
import { useGetPizzaIngredients } from '@agos/api-client/menu'
import { Loader2, Plus, Save, Trash2 } from 'lucide-react'
import { useState } from 'react'

/**
 * The crusts a franchise has configured — §9.134.
 *
 * <p>Matched on the `Okraj` category, which is where §9.61 put them when the crust stopped being a
 * setting and became an ingredient. Named rather than counted inline so the note can be tested: the
 * whole point of the entry is that it states a position, and a position can be wrong.
 */
export function crustNames(ingredients: unknown): string[] {
  if (!Array.isArray(ingredients)) return []
  return ingredients
    .filter((entry) => {
      const row = entry as { category?: unknown; active?: unknown }
      // `active !== false` for §9.131's reason: a row that omits the field is real.
      return row.category === 'Okraj' && row.active !== false
    })
    .map((entry) => {
      const name = (entry as { name?: unknown }).name
      return typeof name === 'string' ? name.trim() : ''
    })
    .filter((name) => name !== '')
}

/** Above this the base margin reads green; below it, amber. Legacy's threshold. */
const HEALTHY_BASE_MARGIN_PERCENT = 60

const FIELD_CLASSES =
  'w-full px-3 py-2 rounded-xl bg-card border border-border text-sm font-bold outline-none focus:ring-2 focus:ring-primary/20'
const LABEL_CLASSES = 'text-xs font-semibold text-muted-foreground block mb-1'

function Switch({
  on,
  onToggle,
  disabled,
  title,
  large,
}: {
  on: boolean
  onToggle: () => void
  disabled?: boolean
  title?: string
  large?: boolean
}) {
  return (
    <button
      type="button"
      role="switch"
      aria-checked={on}
      aria-label={title}
      title={title}
      disabled={disabled}
      onClick={onToggle}
      className={cn(
        'rounded-full transition-all relative shrink-0 disabled:opacity-50',
        large ? 'w-12 h-6' : 'w-10 h-5',
        on ? 'bg-primary' : 'bg-muted',
      )}
    >
      <span
        className={cn(
          'absolute rounded-full bg-white shadow transition-all',
          large ? 'top-1 w-4 h-4' : 'top-0.5 w-4 h-4',
          on ? (large ? 'left-7' : 'left-5') : large ? 'left-1' : 'left-0.5',
        )}
      />
    </button>
  )
}

export function SizesTab({
  config,
  setConfig,
  save,
  isSaving,
  justSaved,
  onGoToIngredients,
}: {
  config: PizzaConfig
  setConfig: (update: (previous: PizzaConfig) => PizzaConfig) => void
  save: () => void
  isSaving: boolean
  justSaved: boolean
  /** Opens the ingredients tab — §9.134. The note is where somebody asks the question. */
  onGoToIngredients?: () => void
}) {
  // §9.134 — what is actually configured, so the note states a position rather than a direction.
  const { data: ingredientData, isSuccess: crustsLoaded } = useGetPizzaIngredients()
  const crusts = crustNames(ingredientData)
  const [newSizeCm, setNewSizeCm] = useState('')
  const [newSizeBasePrice, setNewSizeBasePrice] = useState('')
  const [pendingSizeDeletion, setPendingSizeDeletion] = useState<PizzaSize | null>(null)

  const enabledCount = config.sizes.filter((size) => size.enabled !== false).length
  const proposedCm = Number.parseInt(newSizeCm, 10)
  const proposedExists = config.sizes.some((size) => size.cm === proposedCm)

  const patchSize = (cm: number, patch: Partial<PizzaSize>) =>
    setConfig((previous) => ({
      ...previous,
      sizes: previous.sizes.map((size) => (size.cm === cm ? { ...size, ...patch } : size)),
    }))

  const addSize = () => {
    if (!Number.isFinite(proposedCm) || proposedCm < 10 || proposedCm > 999) return
    if (proposedExists) return

    setConfig((previous) => ({
      ...previous,
      sizes: [
        ...previous.sizes,
        {
          cm: proposedCm,
          label: `${proposedCm} cm`,
          basePrice: Number(newSizeBasePrice) || 0,
          enabled: true,
        },
      ].sort((left, right) => left.cm - right.cm),
    }))
    setNewSizeCm('')
    setNewSizeBasePrice('')
  }

  const removeSize = (cm: number) => {
    setConfig((previous) => ({
      ...previous,
      sizes: previous.sizes.filter((size) => size.cm !== cm),
      // Dropping the kombo size leaves it pointing at nothing; clearing it
      // makes `komboSizeCm` fall back to the largest enabled size.
      ...(previous.komboSizeCm === cm ? { komboSizeCm: undefined } : {}),
    }))
    setPendingSizeDeletion(null)
  }

  return (
    <div className="space-y-5">
      <div className="bg-card border border-border rounded-2xl p-5 space-y-4">
        <div>
          <p className="font-bold text-foreground">
            <T>Veľkosti pizze</T>
          </p>
          <p className="text-xs text-muted-foreground mt-0.5">
            <T>Zapnuté veľkosti sa ponúkajú v kase aj na webe a tvoria stĺpce v záložke Suroviny.</T>
          </p>
        </div>

        <div className="grid grid-cols-1 sm:grid-cols-2 lg:grid-cols-3 gap-3">
          {config.sizes.map((size) => {
            const isEnabled = size.enabled !== false
            const baseMarginPercent =
              size.baseCost != null && size.basePrice > 0
                ? ((size.basePrice - size.baseCost) / size.basePrice) * 100
                : null

            return (
              <div
                key={size.cm}
                className={cn(
                  'bg-background border rounded-2xl p-4 space-y-3',
                  isEnabled ? 'border-border' : 'border-border/50 opacity-60',
                )}
              >
                <div className="flex items-start justify-between gap-2">
                  <div className="min-w-0">
                    <p className="font-bold text-foreground">{size.label}</p>
                    <p className="text-xs text-muted-foreground">
                      <T>Základná pizza</T>
                    </p>
                  </div>
                  <div className="flex items-center gap-1.5 shrink-0">
                    <Switch
                      on={isEnabled}
                      title={isEnabled ? tt('Vypnúť veľkosť') : tt('Zapnúť veľkosť')}
                      // The last enabled size cannot be switched off — a menu
                      // with no sizes cannot sell a pizza.
                      disabled={isEnabled && enabledCount <= 1}
                      onToggle={() => patchSize(size.cm, { enabled: !isEnabled })}
                    />
                    <button
                      type="button"
                      aria-label={tt('Zmazať veľkosť')}
                      disabled={config.sizes.length <= 1}
                      onClick={() => setPendingSizeDeletion(size)}
                      className="w-7 h-7 rounded-lg bg-red-500/10 text-red-500 flex items-center justify-center hover:bg-red-500/20 transition-colors disabled:opacity-40"
                    >
                      <Trash2 className="w-3.5 h-3.5" />
                    </button>
                  </div>
                </div>

                <div>
                  <label className={LABEL_CLASSES} htmlFor="size-zakladna-cena">
                    <T>Základná cena (€)</T>
                  </label>
                  <input
                    id="size-zakladna-cena"
                    type="number"
                    step="0.10"
                    min="0"
                    value={size.basePrice}
                    onChange={(event) =>
                      patchSize(size.cm, { basePrice: Number(event.target.value) || 0 })
                    }
                    className={FIELD_CLASSES}
                  />
                </div>

                <div>
                  <label
                    className={LABEL_CLASSES}
                    title={tt(
                      'Surovinová cena cesta + omáčky + základného syra. Používa sa pri výpočte marže dynamickej pizze.',
                    )}
                   htmlFor="size-naklad-zakladu-pre-marzu">
                    <T>Náklad základu (€)</T>{' '}
                    <span className="text-muted-foreground/60 font-normal">
                      <T>- pre maržu</T>
                    </span>
                  </label>
                  <input
                    id="size-naklad-zakladu-pre-marzu"
                    type="number"
                    step="0.0001"
                    min="0"
                    placeholder="-"
                    value={size.baseCost ?? ''}
                    onChange={(event) =>
                      patchSize(size.cm, {
                        baseCost:
                          event.target.value === '' ? undefined : Number(event.target.value) || 0,
                      })
                    }
                    className="w-full px-3 py-2 rounded-xl bg-amber-50 dark:bg-amber-950/20 border border-amber-200 dark:border-amber-900/40 text-sm font-bold outline-none focus:ring-2 focus:ring-amber-400/30"
                  />
                  {baseMarginPercent !== null && (
                    <p className="text-[10px] text-muted-foreground mt-1">
                      <T>Marža základu:</T>{' '}
                      <strong
                        className={
                          baseMarginPercent >= HEALTHY_BASE_MARGIN_PERCENT
                            ? 'text-emerald-600 dark:text-emerald-400'
                            : 'text-amber-600 dark:text-amber-400'
                        }
                      >
                        {formatPercent(baseMarginPercent, 0)}
                      </strong>
                    </p>
                  )}
                </div>
              </div>
            )
          })}

          <div className="bg-background border border-dashed border-border rounded-2xl p-4 space-y-3 flex flex-col justify-center">
            <p className="text-xs font-bold text-muted-foreground uppercase tracking-wider">
              <T>Pridať veľkosť</T>
            </p>
            <div className="flex gap-2">
              <input
                type="number"
                step="1"
                min="10"
                max="999"
                value={newSizeCm}
                onChange={(event) => setNewSizeCm(event.target.value)}
                placeholder="cm"
                aria-label="cm"
                className={cn(FIELD_CLASSES, 'w-20')}
              />
              <input
                type="number"
                step="0.10"
                min="0"
                value={newSizeBasePrice}
                onChange={(event) => setNewSizeBasePrice(event.target.value)}
                placeholder={tt('Základná cena (€)')}
                className={cn(FIELD_CLASSES, 'flex-1 min-w-0')}
              />
            </div>
            {newSizeCm && proposedExists && (
              <p className="text-[10px] text-red-500">
                <T>Táto veľkosť už existuje.</T>
              </p>
            )}
            <button
              type="button"
              onClick={addSize}
              disabled={!newSizeCm || proposedExists}
              className={cn('px-4 py-2 rounded-xl flex items-center justify-center gap-2', BUTTON_PRIMARY, 'text-sm')}
            >
              <Plus className="w-4 h-4" /> <T>Pridať</T>
            </button>
            <p className="text-[10px] text-muted-foreground">
              <T>
                Po pridaní veľkosti doplňte ceny surovín pre túto veľkosť v záložke Suroviny. Kým nie
                sú vyplnené, použije sa najbližšia veľkosť.
              </T>
            </p>
          </div>
        </div>
      </div>

      <div className="bg-card border border-border rounded-2xl p-5 space-y-3">
        <div className="flex items-center justify-between">
          <div>
            <p className="font-bold text-foreground">
              <T>Kombo pizza</T>
            </p>
            <p className="text-xs text-muted-foreground mt-0.5">
              <T>Ponúkať kombo pizzu (3 pizze v jednej krabici) v kase aj na webe.</T>
            </p>
          </div>
          <Switch
            large
            on={isKomboEnabled(config)}
            title={tt('Kombo pizza')}
            onToggle={() =>
              setConfig((previous) => ({ ...previous, komboEnabled: !isKomboEnabled(previous) }))
            }
          />
        </div>
        {isKomboEnabled(config) && (
          <div>
            <p className="text-xs font-semibold text-muted-foreground mb-1.5">
              <T>Veľkosť kombo pizze</T>
            </p>
            <select
              value={String(komboSizeCm(config))}
              onChange={(event) =>
                setConfig((previous) => ({
                  ...previous,
                  komboSizeCm: Number.parseInt(event.target.value, 10),
                }))
              }
              aria-label={tt('Veľkosť kombo pizze')}
              className={cn(FIELD_CLASSES, 'w-44 bg-background')}
            >
              {enabledSizes(config).map((size) => (
                <option key={size.cm} value={size.cm}>
                  {size.label}
                </option>
              ))}
            </select>
          </div>
        )}
      </div>

      {/* §9.60 - which base a new pizza opens with. Naming a base that is no longer offered means
        * *no preselection* rather than an error, so removing a base cannot break the builder. */}
      <div className="bg-card border border-border rounded-2xl p-5 space-y-3">
        <div>
          <p className="font-bold text-foreground">
            <T>Predvolený základ</T>
          </p>
          <p className="text-xs text-muted-foreground mt-0.5">
            <T>Ktorý základ bude v builderi vybraný hneď po otvorení. Zákazník ho môže zmeniť.</T>
          </p>
        </div>
        <select
          value={config.defaultBaseValue ?? ''}
          onChange={(event) =>
            setConfig((previous) => ({
              ...previous,
              defaultBaseValue: event.target.value === '' ? null : event.target.value,
            }))
          }
          aria-label={tt('Predvolený základ')}
          data-default-base
          className={cn(FIELD_CLASSES, 'w-64 bg-background')}
        >
          <option value="">{tt('- žiadny, zákazník vyberie -')}</option>
          {(config.bases ?? []).map((base) => (
            <option key={base.value} value={base.value}>
              {base.label}
            </option>
          ))}
        </select>
      </div>

      {/* §9.93 - where the crust's price now lives, and why this is a note rather than a control.
        *
        * A switch and a price sat here, and **neither did anything after §9.93**. The owner asked for
        * *"the same logic as other ingrediencies"*, so an `Okraj` row became an ordinary ingredient:
        * the builder offers the group whatever this switch said, and charges the row's own per-size
        * price rather than this one. A franchise could type 0,50 € here and be charged 0,00 €.
        *
        * Saying where it went beats both leaving a control that lies and deleting it silently —
        * `garlicCrustEnabled` and `garlicCrustPrice` are still stored and still sent, so nothing is
        * lost if this is ever wanted back. */}
      <div className="bg-card border border-border rounded-2xl p-5" data-crust-note>
        <p className="font-bold text-foreground">
          <T>Okraje</T>
        </p>
        <p className="text-xs text-muted-foreground mt-1">
          <T>
            Okraje sú bežné ingrediencie. Cena sa nastavuje pri každom okraji zvlášť, pre každú
            veľkosť.
          </T>
        </p>
        {/* §9.134 - the note used to stop at *they are somewhere else*, which is the whole of the
          * owner's *nerozumiem tomu*. It now says what is actually configured, and offers the way
          * there. Silence while loading: *žiadne* would be a claim the screen cannot yet make. */}
        {!crustsLoaded ? null : crusts.length === 0 ? (
          <p className="text-xs text-amber-600 dark:text-amber-400 font-semibold mt-2" data-crust-none>
            <T>Zatiaľ nemáte nastavený žiadny okraj - pridajte ho v Ingredienciách, kategória „Okraj“.</T>
          </p>
        ) : (
          <p className="text-xs text-foreground mt-2" data-crust-list>
            <T>Nastavené okraje</T> ({crusts.length}): {crusts.join(', ')}
          </p>
        )}
        {onGoToIngredients !== undefined && (
          <button
            type="button"
            onClick={onGoToIngredients}
            data-crust-goto
            className="mt-3 text-xs font-bold text-primary hover:underline"
          >
            <T>Otvoriť Ingrediencie</T>
          </button>
        )}
      </div>

      <button
        type="button"
        onClick={save}
        disabled={isSaving}
        className={cn(
          'flex items-center gap-2 px-6 py-3 rounded-xl font-bold text-sm transition-all disabled:opacity-50',
          justSaved ? 'bg-green-500 text-white' : 'bg-primary text-primary-foreground hover:shadow-md',
        )}
      >
        {isSaving ? <Loader2 className="w-4 h-4 animate-spin" /> : <Save className="w-4 h-4" />}
        {justSaved ? tt('Uložené!') : tt('Uložiť nastavenia')}
      </button>

      <ConfirmDialog
        open={pendingSizeDeletion !== null}
        onClose={() => setPendingSizeDeletion(null)}
        onConfirm={() => pendingSizeDeletion && removeSize(pendingSizeDeletion.cm)}
        variant="danger"
        title={tt('Zmazať veľkosť?')}
        description={`${tt('Veľkosť')} ${pendingSizeDeletion?.label ?? ''} ${tt('sa prestane ponúkať. Ceny surovín pre túto veľkosť zostanú uložené. Zmena sa uloží až po kliknutí na Uložiť nastavenia.')}`}
        confirmLabel={tt('Zmazať')}
      />
    </div>
  )
}
